import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, CheckCircle, Info } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ─── Weight display bar ───────────────────────────────────────────────────────
const WeightBar = ({ mcq = 15, fib = 10, sjt = 75 }) => (
  <div className="flex rounded-xl overflow-hidden h-8 mb-1 shadow-inner">
    <div style={{ width: `${mcq}%` }} className="bg-blue-500 flex items-center justify-center text-white text-xs font-black">{mcq}%</div>
    <div style={{ width: `${fib}%` }} className="bg-emerald-500 flex items-center justify-center text-white text-xs font-black">{fib}%</div>
    <div style={{ width: `${sjt}%` }} className="bg-violet-600 flex items-center justify-center text-white text-xs font-black">{sjt}%</div>
  </div>
);

// ─── MCQ Question Row ─────────────────────────────────────────────────────────
const MCQRow = ({ q, i, onUpdate, onRemove }) => (
  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 space-y-3">
    <div className="flex items-start gap-3">
      <span className="w-7 h-7 bg-blue-600 text-white text-xs font-black rounded-lg flex items-center justify-center flex-shrink-0">{i + 1}</span>
      <textarea rows={2} value={q.question || ''} onChange={e => onUpdate(i, 'question', e.target.value)}
        placeholder="Write the MCQ question…"
        className="flex-1 px-3 py-2 border border-blue-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 resize-none" />
      <button type="button" onClick={() => onRemove(i)} className="text-red-400 hover:text-red-600 mt-1"><Trash2 className="w-4 h-4" /></button>
    </div>
    <div className="grid grid-cols-2 gap-2 ml-10">
      {['A', 'B', 'C', 'D'].map(opt => (
        <div key={opt} className="flex items-center gap-2">
          <button type="button" onClick={() => onUpdate(i, 'correctAnswer', opt)}
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-black flex-shrink-0 transition-all
              ${q.correctAnswer === opt ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300 text-gray-400 hover:border-blue-400'}`}>
            {opt}
          </button>
          <input type="text" value={q.options?.[['A','B','C','D'].indexOf(opt)] || ''} onChange={e => {
            const opts = ['A','B','C','D'].map((o, oi) => oi === ['A','B','C','D'].indexOf(opt) ? e.target.value : (q.options?.[oi] || ''));
            onUpdate(i, 'options', opts);
          }} placeholder={`Option ${opt}`}
            className="flex-1 px-2 py-1 border border-blue-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-400" />
        </div>
      ))}
    </div>
    <div className="flex items-center gap-3 ml-10 flex-wrap">
      <select value={q.quotient || 'CRQ'} onChange={e => onUpdate(i, 'quotient', e.target.value)}
        className="text-xs px-2 py-1 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 bg-white">
        {['CRQ','SRQ','LQ','SIQ','PEQ','DAQ','SEQ'].map(q => <option key={q} value={q}>{q}</option>)}
      </select>
      <select value={q.difficultyLevel || 'L1'} onChange={e => onUpdate(i, 'difficultyLevel', e.target.value)}
        className="text-xs px-2 py-1 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 bg-white">
        {['L1','L2','L3','L4'].map(l => <option key={l} value={l}>{l}</option>)}
      </select>
      <label className="text-xs text-blue-700 font-semibold">Points:
        <input type="number" min={1} max={5} value={q.points || 1} onChange={e => onUpdate(i, 'points', parseInt(e.target.value))}
          className="ml-1 w-12 px-1 py-0.5 border border-blue-200 rounded text-xs text-center" />
      </label>
    </div>
  </div>
);

// ─── FIB Question Row ─────────────────────────────────────────────────────────
const FIBRow = ({ q, i, onUpdate, onRemove }) => (
  <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 space-y-3">
    <div className="flex items-start gap-3">
      <span className="w-7 h-7 bg-emerald-600 text-white text-xs font-black rounded-lg flex items-center justify-center flex-shrink-0">{i + 1}</span>
      <div className="flex-1 space-y-2">
        <input type="text" value={q.questionText || ''} onChange={e => onUpdate(i, 'questionText', e.target.value)}
          placeholder="Write sentence with ___ for blank. e.g. The capital of France is ___"
          className="w-full px-3 py-2 border border-emerald-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-400" />
        <div className="grid grid-cols-2 gap-2">
          <input type="text" value={q.correctAnswer || ''} onChange={e => onUpdate(i, 'correctAnswer', e.target.value)}
            placeholder="Correct answer"
            className="px-2 py-1.5 border border-emerald-200 rounded-lg text-sm font-semibold focus:ring-1 focus:ring-emerald-400" />
          <input type="text" value={q.hint || ''} onChange={e => onUpdate(i, 'hint', e.target.value)}
            placeholder="Optional hint (shown on request)"
            className="px-2 py-1.5 border border-emerald-200 rounded-lg text-sm focus:ring-1 focus:ring-emerald-400" />
        </div>
        <input type="text" value={(q.acceptableAnswers || []).join(', ')} onChange={e => onUpdate(i, 'acceptableAnswers', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
          placeholder="Other accepted answers (comma separated)"
          className="w-full px-2 py-1.5 border border-emerald-200 rounded-lg text-xs focus:ring-1 focus:ring-emerald-400" />
      </div>
      <button type="button" onClick={() => onRemove(i)} className="text-red-400 hover:text-red-600 mt-1"><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
);

// ─── SJT Question Row ─────────────────────────────────────────────────────────
const SJTRow = ({ q, i, onUpdate, onRemove }) => {
  const opts = q.options || [{ label: 'A', text: '', score: 4 }, { label: 'B', text: '', score: 3 }, { label: 'C', text: '', score: 2 }, { label: 'D', text: '', score: 1 }];
  const updateOpt = (oi, field, val) => {
    const updated = opts.map((o, idx) => idx === oi ? { ...o, [field]: val } : o);
    onUpdate(i, 'options', updated);
  };
  return (
    <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className="w-7 h-7 bg-violet-600 text-white text-xs font-black rounded-lg flex items-center justify-center flex-shrink-0">{i + 1}</span>
        <div className="flex-1 space-y-2">
          {/* Scenario */}
          <textarea rows={3} value={q.scenario || ''} onChange={e => onUpdate(i, 'scenario', e.target.value)}
            placeholder="Describe the situation / scenario the student is in…"
            className="w-full px-3 py-2 border border-violet-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-400 resize-none bg-white" />
          {/* Question */}
          <input type="text" value={q.question || ''} onChange={e => onUpdate(i, 'question', e.target.value)}
            placeholder="What should you do? / What is the BEST response?"
            className="w-full px-3 py-2 border border-violet-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-400" />
        </div>
        <button type="button" onClick={() => onRemove(i)} className="text-red-400 hover:text-red-600 mt-1"><Trash2 className="w-4 h-4" /></button>
      </div>

      {/* Options with partial scoring */}
      <div className="space-y-2 ml-10">
        <p className="text-xs font-bold text-violet-600 uppercase tracking-wider">Options (assign score 0–4, best = highest)</p>
        {opts.map((opt, oi) => (
          <div key={oi} className="flex items-center gap-2">
            <button type="button" onClick={() => onUpdate(i, 'bestAnswer', opt.label)}
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-black flex-shrink-0 transition-all
                ${q.bestAnswer === opt.label ? 'border-violet-600 bg-violet-600 text-white' : 'border-violet-300 text-violet-400 hover:border-violet-500'}`}>
              {opt.label}
            </button>
            <input type="text" value={opt.text} onChange={e => updateOpt(oi, 'text', e.target.value)}
              placeholder={`Option ${opt.label}…`}
              className="flex-1 px-2 py-1 border border-violet-200 rounded-lg text-xs focus:ring-1 focus:ring-violet-400" />
            <div className="flex items-center gap-1">
              <span className="text-xs text-violet-500 font-bold">Score:</span>
              <input type="number" min={0} max={4} value={opt.score || 0} onChange={e => updateOpt(oi, 'score', parseInt(e.target.value) || 0)}
                className="w-12 px-1 py-0.5 border border-violet-200 rounded text-xs text-center focus:ring-1 focus:ring-violet-400" />
            </div>
          </div>
        ))}
      </div>

      {/* SJT metadata */}
      <div className="flex items-center gap-3 ml-10 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-violet-600">Best Answer:</span>
          <span className="px-2 py-0.5 bg-violet-600 text-white rounded-full text-xs font-black">{q.bestAnswer || '—'}</span>
          <span className="text-xs text-gray-400">(click radio above)</span>
        </div>
        <select value={q.quotient || 'CRQ'} onChange={e => onUpdate(i, 'quotient', e.target.value)}
          className="text-xs px-2 py-1 border border-violet-200 rounded-lg focus:ring-1 focus:ring-violet-400 bg-white">
          {['CRQ','SRQ','LQ','SIQ','PEQ','DAQ','SEQ'].map(qc => <option key={qc} value={qc}>{qc}</option>)}
        </select>
      </div>

      {/* Explanation */}
      <input type="text" value={q.explanation || ''} onChange={e => onUpdate(i, 'explanation', e.target.value)}
        placeholder="Explanation of the best answer (shown after submission)…"
        className="w-full ml-10 px-3 py-2 border border-violet-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-400" style={{ width: 'calc(100% - 2.5rem)' }} />
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AssessmentBuilder = () => {
  const { id } = useParams(); // courseId
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [assessment, setAssessment] = useState({ isEnabled: true, passingScore: 70, maxAttempts: 3, timeLimit: 60, mcq: { weight: 15, questions: [] }, fib: { weight: 10, questions: [] }, sjt: { weight: 75, questions: [] } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('mcq');

  useEffect(() => { if (id) fetchCourse(); }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/courses/${id}`);
      if (data.success) {
        setCourse(data.data);
        if (data.data.courseAssessment?.isEnabled !== undefined) {
          setAssessment(prev => ({ ...prev, ...data.data.courseAssessment }));
        }
      }
    } catch (e) { toast.error('Failed to load.'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/courses/${id}`, { courseAssessment: assessment });
      toast.success('Assessment saved!');
    } catch (e) { toast.error('Save failed.'); }
    finally { setSaving(false); }
  };

  // Generic question CRUD helpers
  const addQ = (section, template) => setAssessment(prev => ({ ...prev, [section]: { ...prev[section], questions: [...(prev[section].questions || []), { ...template, questionId: `Q${Date.now()}` }] } }));
  const removeQ = (section, i) => setAssessment(prev => ({ ...prev, [section]: { ...prev[section], questions: prev[section].questions.filter((_, idx) => idx !== i) } }));
  const updateQ = (section, i, field, val) => setAssessment(prev => { const qs = [...prev[section].questions]; qs[i] = { ...qs[i], [field]: val }; return { ...prev, [section]: { ...prev[section], questions: qs } }; });

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" /></div>;

  const tabs = [
    { key: 'mcq', label: 'MCQ', weight: 15, count: assessment.mcq?.questions?.length || 0, color: 'text-blue-600 border-blue-500 bg-blue-50', dot: 'bg-blue-500' },
    { key: 'fib', label: 'Fill in Blank', weight: 10, count: assessment.fib?.questions?.length || 0, color: 'text-emerald-600 border-emerald-500 bg-emerald-50', dot: 'bg-emerald-500' },
    { key: 'sjt', label: 'Situational Judgment', weight: 75, count: assessment.sjt?.questions?.length || 0, color: 'text-violet-600 border-violet-500 bg-violet-50', dot: 'bg-violet-600' },
  ];

  return (
    <div className="max-w-3xl mx-auto py-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(`/courses/${id}/builder`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Back to Builder
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Assessment'}
        </button>
      </div>

      {/* Course + weight overview */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-2xl p-6 text-white shadow-xl">
        <p className="text-xs font-bold opacity-70 mb-1">ASSESSMENT BUILDER</p>
        <h1 className="text-xl font-black">{course?.title}</h1>
        <p className="text-violet-200 text-sm">{course?.courseNumber} · Passing score: {assessment.passingScore}%</p>
        <div className="mt-4">
          <p className="text-xs font-bold opacity-70 mb-1.5">SCORE WEIGHTS</p>
          <WeightBar mcq={assessment.mcq?.weight || 15} fib={assessment.fib?.weight || 10} sjt={assessment.sjt?.weight || 75} />
          <div className="flex gap-3 text-xs mt-1 opacity-80">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-300 rounded-full" />MCQ {assessment.mcq?.weight || 15}%</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-300 rounded-full" />FIB {assessment.fib?.weight || 10}%</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-violet-300 rounded-full" />SJT {assessment.sjt?.weight || 75}%</span>
          </div>
        </div>
      </div>

      {/* Settings row */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-bold text-gray-900 mb-4 text-sm">Assessment Settings</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Passing Score (%)', key: 'passingScore', min: 50, max: 100 },
            { label: 'Max Attempts', key: 'maxAttempts', min: 1, max: 5 },
            { label: 'Time Limit (min)', key: 'timeLimit', min: 10, max: 180 },
          ].map(s => (
            <div key={s.key}>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{s.label}</label>
              <input type="number" min={s.min} max={s.max} value={assessment[s.key] || ''}
                onChange={e => setAssessment(prev => ({ ...prev, [s.key]: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-bold text-center focus:ring-2 focus:ring-violet-400" />
            </div>
          ))}
          <div className="flex flex-col justify-end">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Enabled</label>
            <button type="button" onClick={() => setAssessment(prev => ({ ...prev, isEnabled: !prev.isEnabled }))}
              className={`w-full py-2 rounded-xl font-bold text-sm transition-all ${assessment.isEnabled ? 'bg-green-100 text-green-700 border-2 border-green-300' : 'bg-gray-100 text-gray-500 border-2 border-gray-200'}`}>
              {assessment.isEnabled ? '✓ Active' : 'Inactive'}
            </button>
          </div>
        </div>
      </div>

      {/* SJT note */}
      <div className="flex items-start gap-3 bg-violet-50 border border-violet-200 rounded-2xl p-4">
        <Info className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-violet-800">
          <strong>SJT is 75% of the score</strong> — the most critical section. Use realistic workplace/college scenarios. Assign partial scores (0–4) to each option; the highest score option = best answer. Map each question to a Quotient (CRQ, SRQ, LQ…) for Skills Passport calculation.
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map(t => (
            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
              className={`flex-1 py-4 text-sm font-bold transition-colors flex items-center justify-center gap-2
                ${activeTab === t.key ? `border-b-2 ${t.color}` : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
              <span className={`w-2 h-2 rounded-full ${t.dot}`} />
              {t.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-black ${activeTab === t.key ? '' : 'bg-gray-100 text-gray-500'}`}>{t.count}</span>
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {/* MCQ Tab */}
          {activeTab === 'mcq' && (
            <>
              {(assessment.mcq?.questions || []).map((q, i) => (
                <MCQRow key={i} q={q} i={i} onUpdate={(i, f, v) => updateQ('mcq', i, f, v)} onRemove={i => removeQ('mcq', i)} />
              ))}
              <button type="button"
                onClick={() => addQ('mcq', { question: '', type: 'mcq', options: ['', '', '', ''], correctAnswer: 'A', quotient: 'CRQ', difficultyLevel: 'L1', points: 1 })}
                className="w-full py-3 border-2 border-dashed border-blue-300 text-blue-600 rounded-2xl font-bold hover:bg-blue-50 flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Add MCQ Question
              </button>
            </>
          )}

          {/* FIB Tab */}
          {activeTab === 'fib' && (
            <>
              {(assessment.fib?.questions || []).map((q, i) => (
                <FIBRow key={i} q={q} i={i} onUpdate={(i, f, v) => updateQ('fib', i, f, v)} onRemove={i => removeQ('fib', i)} />
              ))}
              <button type="button"
                onClick={() => addQ('fib', { questionText: '', correctAnswer: '', acceptableAnswers: [], hint: '', points: 1 })}
                className="w-full py-3 border-2 border-dashed border-emerald-300 text-emerald-600 rounded-2xl font-bold hover:bg-emerald-50 flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Add FIB Question
              </button>
            </>
          )}

          {/* SJT Tab */}
          {activeTab === 'sjt' && (
            <>
              {(assessment.sjt?.questions || []).map((q, i) => (
                <SJTRow key={i} q={q} i={i} onUpdate={(i, f, v) => updateQ('sjt', i, f, v)} onRemove={i => removeQ('sjt', i)} />
              ))}
              <button type="button"
                onClick={() => addQ('sjt', { scenario: '', question: '', options: [{ label: 'A', text: '', score: 4 }, { label: 'B', text: '', score: 3 }, { label: 'C', text: '', score: 2 }, { label: 'D', text: '', score: 1 }], bestAnswer: 'A', explanation: '', quotient: 'SIQ', difficultyLevel: 'L1', points: 4 })}
                className="w-full py-3 border-2 border-dashed border-violet-300 text-violet-600 rounded-2xl font-bold hover:bg-violet-50 flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Add SJT Question
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary + Save */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center justify-between">
        <div className="flex gap-5 text-sm">
          {tabs.map(t => (
            <div key={t.key} className="text-center">
              <p className="text-2xl font-black text-gray-900">{t.count}</p>
              <p className="text-xs text-gray-400">{t.label}</p>
            </div>
          ))}
          <div className="text-center">
            <p className="text-2xl font-black text-violet-700">{assessment.passingScore}%</p>
            <p className="text-xs text-gray-400">Pass mark</p>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60">
          {saving ? 'Saving…' : '💾 Save All'}
        </button>
      </div>
    </div>
  );
};

export default AssessmentBuilder;
